/**
 * Diminova chat v2.3 — políticas oficiales + catálogo + intención.
 * Persistencia de conversación en localStorage (sobrevive navegación y refresh).
 * Fuente: /policies/shipping-policy y /policies/refund-policy (act. 20 jul 2026).
 */
(function () {
  'use strict';

  const ROOT_ID = 'diminova-assistant';
  const VERSION = '2.3';
  const STORAGE_KEY = 'diminova-assistant-chat-v1';
  const STORAGE_MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000; // 7 días
  const STORAGE_MAX_MESSAGES = 40;

  /** Memoria fija de la tienda (políticas publicadas + contacto) */
  const STORE = {
    name: 'Diminova',
    country: 'Chile',
    onlineOnly: true,
    email: 'contacto@diminova.cl',
    whatsapp: '+56 9 3363 8272',
    policies: {
      shipping: '/policies/shipping-policy',
      refund: '/policies/refund-policy',
    },
    shipping: {
      coverage: 'todo Chile',
      freeChile: true,
      prep: '1 a 5 días hábiles',
      transit: '2 a 10 días hábiles adicionales (según comuna y courier)',
      etaTotal: 'aprox. 3 a 15 días hábiles desde la compra',
      tracking: 'información de seguimiento por email cuando el pedido es despachado',
      costNote: 'el envío a domicilio en Chile es gratis ($0) y se confirma en el checkout antes de pagar',
      damageWindow: '48 horas tras la recepción',
    },
    returns: {
      retractDays: 10,
      retractNote:
        'derecho de retracto: 10 días desde la recepción (producto sin uso y con embalaje original)',
      warrantyMonths: 6,
      warrantyNote:
        'garantía legal: 6 meses desde la recepción (devolución, cambio o reparación gratuita)',
      refundDays: 45,
    },
    payments: ['tarjetas crédito/débito', 'Mercado Pago', 'Webpay', 'Redcompra', 'Apple Pay'],
    security: 'pago online encriptado SSL + comprobante por email',
    categories: [
      'Iluminación',
      'Herramientas Eléctricas',
      'Smart Home / Seguridad',
      'Gadgets',
      'Home',
      'Audio',
    ],
  };

  const STOPWORDS = new Set(
    normalizeTokens(
      'necesito necesita necesitas quiero quieres busco busca buscan tienen tiene hay me una un unos unas el la los las de del al a para por con sin que qué como cómo algo algun alguna algunos mi tu su este esta estos estas mas más bien ya pues po poh please pleasee'
    )
  );

  /** Intenciones específicas (prioridad sobre KB genérica) */
  const INTENTS = [
    {
      id: 'soldadora',
      re: /\b(soldadora|soldador|soldar|soldadura|mig|mma|electrodo|inversora)\b/,
      search: 'soldadora',
      must: /soldad|soldar|mig|mma|electrodo|inversora|tasbel/,
      intro: [
        'Dale, pa soldar te muestro las soldadoras que tenemos ahora:',
        'Ok — soldadoras disponibles en catálogo:',
      ],
    },
    {
      id: 'taladro',
      re: /\b(taladro|atornillador|impacto)\b/,
      search: 'taladro',
      must: /taladro|impacto|atornill/,
      intro: ['Pa taladro/impacto, mira estas opciones:'],
    },
    {
      id: 'pintura',
      re: /\b(pistola de pintura|pintura|pintar)\b/,
      search: 'pistola pintura',
      must: /pintura|pintar/,
      intro: ['Pa pintar, estas pistolas pueden servirte:'],
    },
    {
      id: 'camara',
      re: /\b(camara|cámaras|camaras|seguridad|vigilancia|ptz|alarma)\b/,
      search: 'camara wifi',
      must: /camara|wifi|ptz|seguridad|vigilancia/,
      intro: ['Pa seguridad, te muestro cámaras disponibles:'],
    },
    {
      id: 'iluminacion',
      re: /\b(foco|focos|solar|ilumin|lampara|lámpara|led|patio|jardin|jardín|fachada|porton|portón)\b/,
      search: 'foco solar',
      must: /foco|solar|led|lampara|lámpara|luz|ilumin/,
      intro: [
        'Pa iluminación, mira estas opciones:',
        'Dale — focos / LED disponibles:',
      ],
    },
    {
      id: 'drone',
      re: /\b(drone|dron|drones)\b/,
      search: 'drone',
      must: /dron/,
      intro: ['Pa drones, estas son las opciones:'],
    },
    {
      id: 'audifonos',
      re: /\b(audifono|audífono|auricular|headset)\b/,
      search: 'audifonos',
      must: /audif|auricular|headset/,
      intro: ['Pa audio, mira estos:'],
    },
    {
      id: 'aspiradora',
      re: /\b(aspiradora|robot aspirador)\b/,
      search: 'aspiradora',
      must: /aspir/,
      intro: ['Pa aspiradora, estas opciones:'],
    },
    {
      id: 'powerbank',
      re: /\b(power bank|powerbank|bateria portatil|batería portátil)\b/,
      search: 'power bank',
      must: /power|bater/,
      intro: ['Pa energía portátil, mira estas:'],
    },
    {
      id: 'herramientas',
      re: /\b(herramienta|herramientas)\b/,
      search: 'herramientas electricas',
      must: /herramient|taladro|soldad|impacto|pintura|sierra/,
      intro: ['En herramientas tenemos varias líneas. Mira estas:'],
    },
  ];

  const VOICE = {
    greetings: {
      manana: [
        '¡Buenos días! Acá Diminova. Cuéntame qué andas buscando y te oriento altiro.',
        '¡Buenos días! ¿Producto, envío o una recomendación?',
      ],
      tarde: [
        '¡Buenas tardes! Dime qué necesitas y te ayudo a elegir sin dar vueltas.',
        '¡Buenas tardes! Acá estoy. ¿Producto, precio o despacho?',
      ],
      noche: [
        '¡Buenas noches! Qué bueno que escribas. Cuéntame qué estás buscando y te oriento.',
        '¡Buenas noches! Acá Diminova. ¿Te ayudo con un producto, envío o una recomendación?',
      ],
      generic: [
        '¡Hola! Acá Diminova. Cuéntame qué andas buscando y te oriento.',
        '¡Hola! ¿Producto, envío o una recomendación? Aquí estoy.',
      ],
    },
    thanks: [
      'Dale, con gusto. Si quieres, miramos otra o te ayudo a cerrar la compra.',
      'Bacán. Cualquier otra duda, aquí estoy.',
      'De nada. Si te late alguna, ábrela y la revisamos.',
    ],
    identity: [
      'Soy <strong>Diminova</strong>, de la tienda. Te ayudo con productos, precios, envíos a Chile, pagos, cambios y reclamos. Dime qué buscas y te acompaño.',
    ],
    vendorRedirect: [
      'Acá te atiende el equipo de <strong>Diminova</strong>. Cuéntame qué producto necesitas y te muestro opciones con precio.',
    ],
    fallback: [
      'Pa orientarte bien, dime si buscas <strong>iluminación</strong>, <strong>soldadora</strong>, <strong>cámaras</strong>, <strong>taladro</strong> u otro gadget. También te ayudo con envíos, pagos o reclamos.',
    ],
    error: [
      'Disculpa, se me cruzó algo. Cuéntame de nuevo qué necesitas — por ejemplo soldadora, foco solar o cámara — y te oriento.',
    ],
    productClosers: [
      'Si me dices presupuesto o pa qué lo quieres, te afino cuál conviene más.',
      'Abre la ficha de la que te guste. Si quieres, te digo cuál me parece mejor.',
      '¿Quieres la más conveniente de precio, o la más completa?',
    ],
    reShow: ['Claro, te dejo de nuevo las que estábamos viendo:', 'Ya, aquí van otra vez:'],
    cheapest: [
      (t, p) =>
        `Entre las que te mostré, la más conveniente es <strong>${t}</strong>${p ? ` a <strong>${p}</strong>` : ''}.`,
      (t, p) =>
        `Si vas por precio, me quedo con <strong>${t}</strong>${p ? ` (${p})` : ''}.`,
    ],
    budgetAck: [
      (b) => `Con un presupuesto cerca de <strong>${b}</strong>, te filtro lo más cercano:`,
      (b) => `Ok, alrededor de <strong>${b}</strong>. Estas se acercan:`,
    ],
    greetingAck: ['¡Hola! ', 'Hola — ', 'Buenas — '],
    noStockMatch: [
      'Ahora mismo no me aparecen opciones claras pa eso. ¿Me das otra palabra (marca, uso o presupuesto) y lo busco de nuevo?',
    ],
  };

  function $(sel, root) {
    return (root || document).querySelector(sel);
  }

  function pick(arr) {
    return arr[Math.floor(Math.random() * arr.length)];
  }

  function sleep(ms) {
    return new Promise((r) => setTimeout(r, ms));
  }

  function normalizeTokens(text) {
    return normalize(text).split(' ').filter(Boolean);
  }

  function moneyCLP(amount, fromCents) {
    if (amount == null || Number.isNaN(Number(amount))) return null;
    let n = Number(amount);
    if (fromCents) n = Math.round(n / 100);
    else n = Math.round(n);
    return '$' + n.toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  }

  function priceNumber(p) {
    let n = Number(p.price);
    if (Number.isNaN(n)) return Infinity;
    if (p.priceFromCents) n = n / 100;
    return n;
  }

  function normalize(text) {
    return String(text || '')
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^\w\s]/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  }

  function tokenize(text) {
    return normalize(text)
      .split(' ')
      .filter((t) => t.length > 2);
  }

  function scoreOverlap(queryTokens, haystack) {
    const h = normalize(haystack);
    if (!h) return 0;
    let score = 0;
    for (const t of queryTokens) {
      if (h.includes(t)) score += t.length > 4 ? 2 : 1;
    }
    return score;
  }

  function chileHour() {
    try {
      const parts = new Intl.DateTimeFormat('en-US', {
        timeZone: 'America/Santiago',
        hour: 'numeric',
        hour12: false,
      }).formatToParts(new Date());
      return Number(parts.find((p) => p.type === 'hour')?.value || 12);
    } catch {
      return new Date().getHours();
    }
  }

  function greetingBucket() {
    const h = chileHour();
    if (h >= 6 && h < 12) return 'manana';
    if (h >= 12 && h < 20) return 'tarde';
    return 'noche';
  }

  function isPureGreeting(q) {
    const n = normalize(q);
    if (!n) return false;
    if (
      /^(hola|holi|holaa|hola hola|buenas|buen dia|buenos dias|buena tarde|buenas tardes|buena noche|buenas noches|hey|hi|hello|que tal|como estas|como esta|como andai|como anda|como te va|que onda|wena|wenas|wena tardes|wena noches)( po| poh)?( diminova)?[!?.]*$/.test(
        n
      )
    ) {
      return true;
    }
    return /^(solo )?(paso|pasaba|queria) a saludar[!?.]*$/.test(n);
  }

  function startsWithGreeting(q) {
    return /^(hola|holi|buenas|buen(?:os)?\s*d[ií]as?|buen(?:as)?\s*tardes?|buen(?:as)?\s*noches?|wena|wenas)\b/i.test(
      q.trim()
    );
  }

  function stripGreetingPrefix(q) {
    return q
      .replace(
        /^(hola|holi|holaa|wena|wenas|buenas\s+noches|buena\s+noche|buenas\s+tardes|buena\s+tarde|buenos\s+d[ií]as|buen\s+d[ií]a|buenas)(?:[,!.\s]+|$)/i,
        ''
      )
      .trim();
  }

  function isThanks(q) {
    const n = normalize(q);
    return /^(gracias|muchas gracias|thank you|perfecto|listo|dale|ok|okey|super|bacan|bkn|genial|excelente|wena|la raja)( po| poh)?[!?.]*$/.test(
      n
    );
  }

  function isIdentity(q) {
    return /(eres un bot|sos un bot|eres ia|inteligencia artificial|chatbot|robot|quien eres|quien sos|sos real|eres real|atencion automatica|eres humana|eres una persona)/.test(
      normalize(q)
    );
  }

  function isCheapest(q) {
    return /(mas barato|más barato|mas economico|más económico|el mas barato|el más barato|menor precio|el mas conveniente|la mas barata|mas conveniente)/.test(
      normalize(q)
    );
  }

  function isBuyIntent(q) {
    const n = normalize(q);
    return /(comprar|compra|busco|necesito|quiero|tienen|hay|precio|cuesta|vale|stock|recomend|mostrar|opcion|catalogo|producto|foco|camara|drone|soldadora|soldador|soldar|pistola|ilumin|solar|\bled\b|herramient|kit|bater|wifi|seguridad|patio|jardin|exterior|interior|taladro|audifono|aspiradora|me sirve|me conviene|fachada|porton)/.test(
      n
    );
  }

  function wantsNightLighting(q) {
    const n = normalize(q);
    if (/\bbuenas?\s+noches?\b/.test(n) && !/(patio|jardin|exterior|fachada|porton|ilumin|foco|solar|luz)/.test(n)) {
      return false;
    }
    return /(patio|jardin|exterior|fachada|porton|luz solar|foco solar|iluminar|iluminacion)/.test(n);
  }

  function detectIntent(q) {
    const n = normalize(q);
    for (const intent of INTENTS) {
      if (intent.re.test(n)) return intent;
    }
    if (wantsNightLighting(q)) return INTENTS.find((i) => i.id === 'iluminacion');
    return null;
  }

  /** Quita relleno: "necesito una soldadora" → "soldadora" */
  function cleanSearchQuery(q) {
    const tokens = normalize(q)
      .split(' ')
      .filter((t) => t && t.length > 1 && !STOPWORDS.has(t));
    return tokens.join(' ').trim();
  }

  function extractBudget(q) {
    const n = normalize(q);
    let m = n.match(
      /(?:presupuesto|hasta|maximo|máximo|cerca de|como|como de|unos?|alrededor de)\s*\$?\s*(\d{1,3}(?:[.\s]\d{3})+|\d+)\s*(mil|lucas)?/
    );
    if (!m) m = n.match(/\$\s*(\d{1,3}(?:[.\s]\d{3})+|\d+)\s*(mil|lucas)?/);
    if (!m) m = n.match(/\b(\d{1,3})\s*(lucas|mil)\b/);
    if (!m) m = n.match(/\b(\d{4,7})\b/);
    if (!m) return null;
    let num = String(m[1]).replace(/[.\s]/g, '');
    let val = parseInt(num, 10);
    if (Number.isNaN(val)) return null;
    if (m[2] && /lucas|mil/.test(m[2])) val *= 1000;
    if (val < 1000 || val > 5000000) return null;
    return val;
  }

  function escapeHtml(s) {
    return String(s)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  function pickGreeting() {
    const bucket = greetingBucket();
    return pick(VOICE.greetings[bucket] || VOICE.greetings.generic);
  }

  /** Respuestas de memoria de tienda (alineadas a políticas publicadas) */
  function matchStoreFact(q) {
    const n = normalize(q);
    const policyShipping = STORE.policies.shipping;
    const policyRefund = STORE.policies.refund;

    if (
      /(reclamo|reclamar|problema con mi pedido|no me llego|no me llegó|paquete perdido|pedido atrasado|pedido demorado)/.test(
        n
      )
    ) {
      return `Para un reclamo, escribe a <strong>${STORE.email}</strong> o WhatsApp <strong>${STORE.whatsapp}</strong> con tu <strong>nombre, nº de pedido y producto</strong>. Si llegó quebrado o en mal estado, envía un <strong>video</strong> del producto y de cómo llegó el envío. Idealmente avísanos dentro de las <strong>${STORE.shipping.damageWindow}</strong>. Detalle: <a href="${policyRefund}">política de reembolso</a> / <a href="${policyShipping}">envío</a>. Cuéntame acá qué pasó y te oriento.`;
    }
    if (/(contacto|whatsapp|correo|email|mail|hablar con|numero|teléfono|telefono)/.test(n)) {
      return `Puedes escribirme acá en el chat. También: email <strong>${STORE.email}</strong> y WhatsApp <strong>${STORE.whatsapp}</strong>. Operamos 100% online en Chile.`;
    }
    if (/(envio|despacho|seguimiento|cuando llega|demora el envio|plazo de entrega|cuesta el envio|envio gratis)/.test(n)) {
      return `Despachamos a <strong>${STORE.shipping.coverage}</strong>. ${STORE.shipping.costNote.charAt(0).toUpperCase() + STORE.shipping.costNote.slice(1)}. Preparación: <strong>${STORE.shipping.prep}</strong>; tránsito: <strong>${STORE.shipping.transit}</strong>. Rango habitual total: <strong>${STORE.shipping.etaTotal}</strong>. Te llega ${STORE.shipping.tracking}. Detalle: <a href="${policyShipping}">política de envío</a>.`;
    }
    if (/(pago|webpay|mercado pago|tarjeta|redcompra|apple pay)/.test(n)) {
      return `Aceptamos ${STORE.payments.join(', ')}. El pago es online al finalizar la compra, con ${STORE.security}.`;
    }
    if (/(seguro|estafa|confianza|ssl|legitimo)/.test(n)) {
      return `Sí, compras en Diminova con ${STORE.security}. Si tienes cualquier duda antes de pagar, pregúntame.`;
    }
    if (/(retracto|arrepent)/.test(n)) {
      return `En compras online puedes ejercer el <strong>${STORE.returns.retractNote}</strong>. Escríbenos a <strong>${STORE.email}</strong> con nombre, nº de pedido, producto y motivo. El reembolso se gestiona sin retención de gastos, dentro del plazo legal (hasta ${STORE.returns.refundDays} días desde tu aviso). Detalle: <a href="${policyRefund}">política de reembolso</a>.`;
    }
    if (/(garantia legal|garantía legal|meses de garantia|meses de garantía)/.test(n)) {
      return `Si el producto nuevo tiene falla de fábrica, opera la <strong>${STORE.returns.warrantyNote}</strong>. Puedes elegir devolución, cambio o reparación. Escríbenos a <strong>${STORE.email}</strong> con nº de pedido, fotos y comprobante. Detalle: <a href="${policyRefund}">política de reembolso</a>.`;
    }
    if (/(cambio|devolucion|garantia|reembolso|falla|roto|equivocado|danado|dañado)/.test(n)) {
      return `Si llegó quebrado o en mal estado, envíanos un <strong>video del producto y de cómo llegó el envío</strong>, con todos tus datos (nombre, nº de pedido, producto y qué ocurrió) a <strong>${STORE.email}</strong> o WhatsApp <strong>${STORE.whatsapp}</strong>. También aplica <strong>retracto</strong> (${STORE.returns.retractDays} días, sin uso y con embalaje) o <strong>garantía legal</strong> (${STORE.returns.warrantyMonths} meses por falla). Detalle: <a href="${policyRefund}">política de reembolso</a>.`;
    }
    if (/(factura|boleta|comprobante|sii)/.test(n)) {
      return `Al comprar recibes comprobante por email. Si necesitas un dato específico de boleta/factura, escríbenos a <strong>${STORE.email}</strong> después de la compra y te orientamos.`;
    }
    if (/(horario|cuando atienden|estan online)/.test(n)) {
      return `Puedes escribirme cuando estés en la tienda y te respondo al momento. Para temas de pedido/reclamo también está el email y WhatsApp del pie.`;
    }
    if (/(quienes son|quien es diminova|de donde son|tienda fisica|local)/.test(n)) {
      return `Somos <strong>Diminova</strong>, tienda online chilena de ${STORE.categories.slice(0, 4).join(', ').toLowerCase()} y más. Operamos 100% online, con despacho a todo Chile y soporte real.`;
    }
    if (/(como comprar|como pago|carrito|checkout)/.test(n)) {
      return `Eliges el producto → lo agregas al carrito → completas tus datos → pagas online. Antes de confirmar ves el total; en Chile el envío a domicilio figura como <strong>gratis</strong> bajo el método vigente. Si quieres, te muestro un producto y te guío.`;
    }
    return null;
  }

  class DiminovaAssistant {
    constructor(root) {
      this.root = root;
      this.cfg = this.readConfig();
      this.open = false;
      this.busy = false;
      this.lastProducts = [];
      this.topic = null;
      this.budget = null;
      this.turns = 0;
      this.saidHi = false;
      this.history = [];
      this.catalog = [];
      this.catalogReady = null;
      this.fab = $('[data-da-fab]', root);
      this.panel = $('[data-da-panel]', root);
      this.messages = $('[data-da-messages]', root);
      this.form = $('[data-da-form]', root);
      this.input = $('[data-da-input]', root);
      this.closeBtn = $('[data-da-close]', root);
      this.statusEl = $('[data-da-status]', root);
      this.bind();
      this.boot();
    }

    readConfig() {
      const el = $('[data-da-config]', this.root);
      try {
        return JSON.parse(el?.textContent || '{}');
      } catch {
        return {};
      }
    }

    loadState() {
      try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (!raw) return null;
        const data = JSON.parse(raw);
        if (!data || data.schema !== 1) return null;
        if (!data.savedAt || Date.now() - data.savedAt > STORAGE_MAX_AGE_MS) {
          localStorage.removeItem(STORAGE_KEY);
          return null;
        }
        if (!Array.isArray(data.messages) || !data.messages.length) return null;
        return data;
      } catch {
        return null;
      }
    }

    saveState() {
      try {
        // Solo persistir si ya hubo interacción real (no solo el welcome).
        const hasUser = this.history.some((m) => m.role === 'user');
        if (!hasUser) return;
        const payload = {
          schema: 1,
          appVersion: VERSION,
          savedAt: Date.now(),
          open: this.open,
          turns: this.turns,
          saidHi: this.saidHi,
          topic: this.topic,
          budget: this.budget,
          lastProducts: Array.isArray(this.lastProducts) ? this.lastProducts.slice(0, 5) : [],
          messages: this.history.slice(-STORAGE_MAX_MESSAGES),
        };
        localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
      } catch {
        // Quota / private mode: ignore
      }
    }

    clearState() {
      try {
        localStorage.removeItem(STORAGE_KEY);
      } catch {
        /* ignore */
      }
      this.history = [];
    }

    restoreFrom(data) {
      this.turns = data.turns || 0;
      this.saidHi = !!data.saidHi;
      this.topic = data.topic || null;
      this.budget = typeof data.budget === 'number' ? data.budget : null;
      this.lastProducts = Array.isArray(data.lastProducts) ? data.lastProducts : [];
      this.history = [];
      if (this.messages) this.messages.innerHTML = '';
      for (const m of data.messages) {
        if (!m || !m.role) continue;
        if (m.role === 'user' && m.text) {
          this.addUser(m.text, { instant: true, skipPersist: true });
        } else if (m.role === 'bot' && m.html) {
          this.addBot(m.html, { instant: true, skipPersist: true });
        }
      }
      // Reabrir si el cliente lo tenía abierto al salir de la página.
      if (data.open) this.toggle(true);
      this.scrollBottom();
    }

    bind() {
      this.fab?.addEventListener('click', () => this.toggle(true));
      this.closeBtn?.addEventListener('click', () => this.toggle(false));
      this.form?.addEventListener('submit', (e) => {
        e.preventDefault();
        this.onSubmit();
      });
      this.root.querySelectorAll('[data-da-quick]').forEach((btn) => {
        btn.addEventListener('click', () => {
          const text = btn.getAttribute('data-da-quick') || btn.textContent;
          this.input.value = text;
          this.onSubmit();
        });
      });
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.open) this.toggle(false);
      });
    }

    boot() {
      this.root.dataset.daVersion = VERSION;
      this.catalogReady = this.loadCatalog();
      const saved = this.loadState();
      if (saved) {
        this.restoreFrom(saved);
        return;
      }
      if (this.cfg.welcome) this.addBot(this.cfg.welcome, { instant: true, skipPersist: true });
    }

    async loadCatalog() {
      const url = this.cfg.catalogUrl;
      if (!url) return;
      try {
        const res = await fetch(url, { headers: { Accept: 'application/json' } });
        if (!res.ok) return;
        const data = await res.json();
        this.catalog = Array.isArray(data) ? data : [];
      } catch {
        this.catalog = [];
      }
    }

    setStatus(mode) {
      if (!this.statusEl) return;
      if (mode === 'typing') {
        this.statusEl.textContent = 'Escribiendo…';
        this.statusEl.classList.add('is-typing');
      } else {
        this.statusEl.textContent = 'En línea';
        this.statusEl.classList.remove('is-typing');
      }
    }

    toggle(force) {
      this.open = typeof force === 'boolean' ? force : !this.open;
      this.root.classList.toggle('is-open', this.open);
      this.fab?.setAttribute('aria-expanded', this.open ? 'true' : 'false');
      this.panel?.setAttribute('aria-hidden', this.open ? 'false' : 'true');
      if (this.open) this.input?.focus();
      this.saveState();
    }

    addBot(html, opts) {
      const row = document.createElement('div');
      row.className = 'da-msg da-msg--bot';
      if (!opts?.instant) row.classList.add('da-msg--enter');
      row.innerHTML = `<div class="da-bubble">${html}</div>`;
      this.messages.appendChild(row);
      this.history.push({ role: 'bot', html: String(html || '') });
      if (!opts?.skipPersist) this.saveState();
      this.scrollBottom();
      return row;
    }

    addUser(text, opts) {
      const row = document.createElement('div');
      row.className = 'da-msg da-msg--user';
      if (!opts?.instant) row.classList.add('da-msg--enter');
      row.innerHTML = `<div class="da-bubble"></div>`;
      row.querySelector('.da-bubble').textContent = text;
      this.messages.appendChild(row);
      this.history.push({ role: 'user', text: String(text || '') });
      if (!opts?.skipPersist) this.saveState();
      this.scrollBottom();
    }

    addTyping() {
      const row = document.createElement('div');
      row.className = 'da-msg da-msg--bot da-msg--typing';
      row.innerHTML =
        '<div class="da-bubble da-bubble--typing"><span></span><span></span><span></span></div>';
      this.messages.appendChild(row);
      this.scrollBottom();
      return row;
    }

    scrollBottom() {
      if (this.messages) this.messages.scrollTop = this.messages.scrollHeight;
    }

    async humanPause(html) {
      const plain = String(html || '')
        .replace(/<[^>]+>/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();
      const len = plain.length;
      const readMs = 280 + Math.min(600, this.turns * 30);
      const typeMs = Math.min(1600, Math.max(350, 14 * Math.min(len, 140)));
      await sleep(readMs + typeMs + 50 + Math.random() * 160);
    }

    async onSubmit() {
      const text = (this.input?.value || '').trim();
      if (!text || this.busy) return;
      this.input.value = '';
      this.addUser(text);
      this.busy = true;
      this.setStatus('typing');
      const typing = this.addTyping();
      try {
        if (this.catalogReady) await this.catalogReady;
        const reply = await this.reply(text);
        await this.humanPause(reply.html);
        typing.remove();
        this.addBot(reply.html);
        this.turns += 1;
        this.saveState();
      } catch (err) {
        typing.remove();
        this.addBot(pick(VOICE.error));
      } finally {
        this.setStatus('online');
        this.busy = false;
        this.input?.focus();
      }
    }

    async reply(raw) {
      const q = raw.trim();
      const greeted = startsWithGreeting(q);
      const core = stripGreetingPrefix(q);
      const coreOrQ = core || q;
      const tokens = tokenize(coreOrQ);
      const n = normalize(coreOrQ);
      const budget = extractBudget(q);
      if (budget) this.budget = budget;

      if (isIdentity(q)) {
        return { html: `<p>${pick(VOICE.identity)}</p>` };
      }

      if (isPureGreeting(q) || (!core && greeted)) {
        this.saidHi = true;
        const nn = normalize(q);
        let line;
        if (/buenas?\s+noches?|wena\s+noches?/.test(nn)) line = pick(VOICE.greetings.noche);
        else if (/buenas?\s+tardes?|wena\s+tardes?/.test(nn)) line = pick(VOICE.greetings.tarde);
        else if (/buenos?\s+dias?|buen\s+dia/.test(nn)) line = pick(VOICE.greetings.manana);
        else line = pickGreeting();
        return { html: `<p>${line}</p>` };
      }

      if (isThanks(q) || isThanks(coreOrQ)) {
        return { html: `<p>${pick(VOICE.thanks)}</p>` };
      }

      if (/\b(dropi|shopify)\b/i.test(q)) {
        return { html: `<p>${pick(VOICE.vendorRedirect)}</p>` };
      }

      // Memoria de tienda (políticas / contacto / reclamos) antes que catálogo
      const storeFact = matchStoreFact(coreOrQ);
      if (storeFact && !detectIntent(coreOrQ)) {
        return { html: `<p>${storeFact}</p>` };
      }

      if (isCheapest(q) && this.lastProducts.length) {
        return { html: this.replyCheapest() };
      }

      if (
        this.lastProducts.length &&
        /(esos|esas|ese|esa|el primero|la primera|el segundo|compar|entre estos|de esos|las mismas|otra vez|de nuevo)/.test(
          n
        )
      ) {
        if (/(barato|econom|precio|conviene)/.test(n)) return { html: this.replyCheapest() };
        return {
          html: `<p>${pick(VOICE.reShow)}</p>` + this.formatProducts(this.lastProducts),
        };
      }

      if (budget && this.lastProducts.length && !isBuyIntent(coreOrQ) && tokens.length < 5) {
        return { html: this.replyWithBudget(this.lastProducts, budget) };
      }

      const intent = detectIntent(coreOrQ);
      const kbHit = this.matchKnowledge(tokens, coreOrQ);

      // Si hay hecho de tienda + no es compra de producto
      if (storeFact && !intent && !(kbHit && kbHit.search) && !isBuyIntent(coreOrQ)) {
        return { html: `<p>${storeFact}</p>` };
      }

      const buy = !!(intent || isBuyIntent(coreOrQ) || wantsNightLighting(coreOrQ) || (kbHit && kbHit.search));
      const cleaned = cleanSearchQuery(coreOrQ);
      // PRIORIDAD: intención específica > KB search > query limpia
      const searchQuery = (intent && intent.search) || (kbHit && kbHit.search) || cleaned || null;

      let products = [];
      if (searchQuery && buy) {
        products = await this.findProducts(searchQuery, intent, cleaned || coreOrQ);
      }

      if (
        this.cfg.productHandle &&
        (buy || /este producto|este foco|esta camara|precio|sirve|especific|comprar/.test(n))
      ) {
        const current = await this.fetchProduct(this.cfg.productHandle);
        if (current) {
          products = [current, ...products.filter((p) => p.handle !== current.handle)].slice(0, 5);
        }
      }

      if (products.length && buy) {
        if (this.budget) products = this.sortByBudget(products, this.budget);
        this.lastProducts = products.slice(0, 4);
        this.topic = searchQuery;
        let intro =
          intent
            ? pick(intent.intro)
            : kbHit && kbHit.score >= 3 && kbHit.search
              ? kbHit.answer
              : 'Según lo que me contaste, mira estas opciones:';
        if (this.budget) intro = pick(VOICE.budgetAck)(moneyCLP(this.budget, false));
        let html = `<p>${intro}</p>` + this.formatProducts(this.lastProducts);
        if (greeted && !this.saidHi) {
          this.saidHi = true;
          html = html.replace(/^<p>/, `<p>${pick(VOICE.greetingAck)}`);
        }
        return { html };
      }

      if (kbHit && kbHit.score >= 3 && !kbHit.search) {
        return { html: `<p>${kbHit.answer}</p>` };
      }

      if (storeFact) {
        return { html: `<p>${storeFact}</p>` };
      }

      if (buy) {
        return { html: `<p>${pick(VOICE.noStockMatch)}</p>` };
      }

      return { html: `<p>${pick(VOICE.fallback)}</p>` };
    }

    /**
     * Busca productos: API suggest (query limpia) + índice local del catálogo.
     * Filtra por "must" de la intención pa no mezclar focos con soldadoras.
     */
    async findProducts(searchQuery, intent, rawForLocal) {
      const fromApi = await this.searchProducts(searchQuery);
      const fromLocal = this.searchCatalog(rawForLocal || searchQuery, intent);
      let merged = this.mergeProducts(fromApi, fromLocal);

      if (intent && intent.must) {
        const filtered = merged.filter((p) => intent.must.test(normalize(p.title + ' ' + (p.handle || ''))));
        if (filtered.length) merged = filtered;
      }

      // Si API vacía (ej. "necesito una soldadora"), el local salva
      if (!merged.length && intent) {
        merged = this.searchCatalog(intent.search, intent);
        if (intent.must) {
          merged = merged.filter((p) => intent.must.test(normalize(p.title + ' ' + (p.handle || ''))));
        }
      }

      return merged.slice(0, 5);
    }

    searchCatalog(query, intent) {
      if (!this.catalog.length) return [];
      let qTokens = tokenize(cleanSearchQuery(query) || query);
      if (!qTokens.length && intent) qTokens = tokenize(intent.search);
      const scored = [];
      for (const item of this.catalog) {
        const hay = normalize([item.t, item.h, item.typ, item.tags].join(' '));
        let score = scoreOverlap(qTokens, hay);
        if (intent && intent.must && intent.must.test(hay)) score += 8;
        if (intent && intent.must && !intent.must.test(hay) && intent.id !== 'herramientas') {
          continue;
        }
        if (score <= 0) continue;
        scored.push({
          score,
          product: {
            title: item.t,
            handle: item.h,
            url: '/products/' + item.h,
            price: item.p,
            priceFromCents: false,
            image: null,
            available: item.av !== false,
          },
        });
      }
      scored.sort((a, b) => b.score - a.score || priceNumber(a.product) - priceNumber(b.product));
      return scored.slice(0, 5).map((s) => s.product);
    }

    mergeProducts(a, b) {
      const map = new Map();
      for (const p of [...a, ...b]) {
        if (!p || !p.handle) continue;
        if (!map.has(p.handle)) map.set(p.handle, p);
      }
      return [...map.values()];
    }

    replyCheapest() {
      const sorted = [...this.lastProducts].sort((a, b) => priceNumber(a) - priceNumber(b));
      const best = sorted[0];
      const price = moneyCLP(best.price, !!best.priceFromCents);
      const line = pick(VOICE.cheapest)(escapeHtml(best.title), price);
      return `<p>${line}</p>` + this.formatProducts([best, ...sorted.slice(1, 3)], { softCloser: true });
    }

    replyWithBudget(list, budget) {
      const sorted = this.sortByBudget(list, budget);
      this.lastProducts = sorted.slice(0, 4);
      const intro = pick(VOICE.budgetAck)(moneyCLP(budget, false));
      return `<p>${intro}</p>` + this.formatProducts(this.lastProducts);
    }

    sortByBudget(products, budget) {
      return [...products].sort((a, b) => {
        const da = Math.abs(priceNumber(a) - budget);
        const db = Math.abs(priceNumber(b) - budget);
        return da - db;
      });
    }

    matchKnowledge(tokens, q) {
      const items = this.cfg.knowledge || [];
      const nq = normalize(q);
      let best = null;
      for (const item of items) {
        const hay = [item.q, item.a, ...(item.tags || [])].join(' ');
        let score = scoreOverlap(tokens, hay);
        for (const tag of item.tags || []) {
          const t = normalize(tag);
          if ((t === 'noche' || t === 'noches') && !wantsNightLighting(q)) continue;
          // No dejar que "herramienta" genérica robe a soldadora/taladro
          if (t === 'herramienta' || t === 'herramientas') {
            if (detectIntent(q) && detectIntent(q).id !== 'herramientas') continue;
          }
          if (nq.includes(t)) score += 3;
        }
        if (!best || score > best.score) {
          best = { ...item, answer: item.a, score };
        }
      }
      return best;
    }

    async searchProducts(query) {
      const q = String(query || '')
        .replace(/[^\wáéíóúñüÁÉÍÓÚÑÜ\s-]/gi, ' ')
        .trim();
      if (q.length < 2) return [];
      const params = new URLSearchParams();
      params.set('q', q);
      params.set('resources[type]', 'product');
      params.set('resources[limit]', '6');
      params.set('resources[options][unavailable_products]', 'last');
      params.set('resources[options][fields]', 'title,product_type,variants.title,tag');

      const res = await fetch('/search/suggest.json?' + params.toString(), {
        headers: { Accept: 'application/json' },
      });
      if (!res.ok) return [];
      const data = await res.json();
      const list = data?.resources?.results?.products || [];
      return list.map((p) => ({
        title: p.title,
        handle: p.handle,
        url: p.url,
        price: p.price,
        priceFromCents: false,
        image: p.image,
        available: p.available,
      }));
    }

    async fetchProduct(handle) {
      try {
        const res = await fetch('/products/' + encodeURIComponent(handle) + '.js');
        if (!res.ok) return null;
        const p = await res.json();
        return {
          title: p.title,
          handle: p.handle,
          url: '/products/' + p.handle,
          price: p.price,
          priceFromCents: true,
          image: p.featured_image,
          available: p.available,
        };
      } catch {
        return null;
      }
    }

    formatProducts(products, opts) {
      const cards = products
        .slice(0, 4)
        .map((p) => {
          const price = moneyCLP(p.price, !!p.priceFromCents);
          const stock = p.available === false ? 'Consultar stock' : 'Disponible';
          const img = p.image
            ? `<img src="${p.image}" alt="" width="48" height="48" loading="lazy">`
            : '';
          return `<a class="da-product" href="${p.url}">
            ${img}
            <span class="da-product__meta">
              <strong>${escapeHtml(p.title)}</strong>
              <span>${price ? price : ''} · ${stock}</span>
            </span>
          </a>`;
        })
        .join('');

      const closer =
        opts?.softCloser || this.turns % 2 === 0
          ? `<p>${pick(VOICE.productClosers)}</p>`
          : '';

      return `<div class="da-products">${cards}</div>${closer}`;
    }
  }

  function init() {
    const root = document.getElementById(ROOT_ID);
    if (!root || root.dataset.daReady) return;
    root.dataset.daReady = '1';
    window.__diminovaAssistant = new DiminovaAssistant(root);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
